import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button';

export interface ErrorBoundaryLabels {
  /** Título do fallback. Ex.: "Algo deu errado". */
  title: string;
  /** Mensagem calma. Ex.: "Recarregue a página para continuar." */
  message: string;
  /** Texto do botão de recarregar. Ex.: "Recarregar página". */
  reloadLabel: string;
}

export interface ErrorBoundaryProps extends ErrorBoundaryLabels {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Captura crashes de render dos filhos e mostra um fallback calmo em vez de
 * tela branca. Texto via props (padrão deste pacote — sem useTranslation
 * interno; cada SPA passa suas strings i18n). Cores herdadas do tema ativo
 * (data-theme dark/light) — o botão é o primitivo Button (theme-aware); o
 * texto herda a cor do tema. Sem hardcode de cor.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log advisório, sem PII. As SPAs admin têm Sentry com beforeSend redactor.
    console.error('[ErrorBoundary]', error.message, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        style={{ maxWidth: 560, margin: '48px auto', padding: 24, textAlign: 'center' }}
      >
        <h2 style={{ marginBottom: 8 }}>{this.props.title}</h2>
        <p style={{ marginBottom: 20 }}>{this.props.message}</p>
        <Button variant="primary" onClick={() => window.location.reload()}>
          {this.props.reloadLabel}
        </Button>
      </div>
    );
  }
}
